import { css, jsx } from '@emotion/react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { Fragment } from 'react';
import { Colors } from '../../../../theme/Colors.es.js';
import '../../../../theme/Shadows.es.js';
import '../../../../theme/Typography.es.js';
import { LegacyTypography } from '../../../../theme/LegacyTypography.es.js';
import { LegacyColorMap } from '../../../../theme/LegacyColorMap.es.js';
import { Heading } from '../../../display/Heading/Heading.es.js';
import '../../../../utils/heading-level-context/HeadingLevelContext.es.js';
import { Icon } from '../../../display/Icon/Icon.es.js';
import { Tag } from '../../../display/Tag/Tag.es.js';
import { Text } from '../../../display/Text/Text.es.js';
import '../../SubscriptionForm/SubscriptionForm.es.js';
import { SubscriptionFormWithDisclaimer } from '../../SubscriptionForm/SubscriptionFormWithDisclaimer.es.js';
import '../../SubscriptionForm/DisclaimerText.es.js';
import { Grid } from '../../../layout/Grid/Grid.es.js';
import { Button } from '../../../navigation/Button/Button.es.js';
import { DarkBackgroundButton } from '../../../navigation/DarkBackgroundButton/DarkBackgroundButton.es.js';
import { mapBackgroundColorToV5TextColorName, mapBackgroundColorToV5HeadingColorName, validateZapierBackgroundAccessibility, getSupportedCTAColor } from '../utils.es.js';

const breakpoints = {
  tablet: 768,
  desktop: 1230
};
const SubscriptionFormStyles = {
  ctaContainer: css`
    max-width: 350px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 470px;
      align-items: stretch;
      flex-direction: column;
    }
  `,
  ctaDisclaimer: css`
    margin: 10px 0;
    text-align: left;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 10px 0;
    }
  `
};
const Styles = labelStyles("HeroFeatures", {
  root: (backgroundColor = "neutral100") => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${Colors[LegacyColorMap[backgroundColor]]};
  `,
  mediaContent: css`
    width: 100%;
    img {
      min-height: 341px;
      object-fit: cover;
    }
  `,
  content: css`
    margin: 0 auto;
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 80px 0;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 85px 0;
    }
    max-width: 770px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  textContent: css`
    margin: auto;
  `,
  labelContainer: css`
    margin-bottom: 10px;
  `,
  heading: (backgroundColor = "neutral100") => css`
    ${LegacyTypography.pageHeader4}
    color: ${Colors[mapBackgroundColorToV5TextColorName(backgroundColor)]};
    margin-bottom: 10px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${LegacyTypography.pageHeader3}
    }
  `,
  description: (backgroundColor = "neutral100") => css`
    ${LegacyTypography.pageHeader9}
    color: ${Colors[mapBackgroundColorToV5HeadingColorName(backgroundColor)]};
    text-align: center;
    margin: 0 auto;
    max-width: 570px;
  `,
  ctaContainer: (isSubscriptionForm = false) => css`
    margin-top: 30px;
    text-align: center;
    width: 100%;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
    }
    ${isSubscriptionForm && withModifier("subscription-form", SubscriptionFormStyles.ctaContainer)}
  `,
  ctaButtonWrapper: css`
    margin: 0 auto;

    // Button should take up full width in mobile only;
    button,
    a {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      button,
      a {
        width: auto;
      }
    }
  `,
  ctaDisclaimer: (isSubscriptionForm = false) => css`
    margin: 10px 0 0 0;
    text-align: center;
    ${isSubscriptionForm && withModifier("subscription-form-disclaimer", SubscriptionFormStyles.ctaDisclaimer)}
  `
});
const HeroFeatures = ({ backgroundColor = "neutral100", ctaColor = "primary", isSubscriptionForm = false, subscriptionPlaceholder = "Email address", heading, description, ctaText, ctaHref, ctaTarget, ctaDisabled = false, ctaDisclaimer, onCtaClick, onCtaKeyDown, children, onSubscriptionFormSubmit, label, labelIcon, labelColor }) => {
  validateZapierBackgroundAccessibility(backgroundColor, isSubscriptionForm, !!ctaDisclaimer);
  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: !isSubscriptionForm ? ctaHref : void 0,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      size: "medium",
      type: isSubscriptionForm ? "submit" : "button",
      "data-testid": "standard-hero-cta-button",
      target: ctaTarget,
      disabled: ctaDisabled
    };
    const color = getSupportedCTAColor(ctaColor, backgroundColor);
    return color === "primary-dark" ? jsx(DarkBackgroundButton, Object.assign({}, sharedProps)) : jsx(Button, Object.assign({}, sharedProps, {
      // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
      color: ctaColor === "primary-alt" ? "primary" : ctaColor
    }));
  };
  return jsx(
    "div",
    { css: Styles.root(backgroundColor), "data-background-color": backgroundColor, "data-zds": true },
    jsx("div", { css: Styles.mediaContent, "data-zds": true }, children),
    jsx(
      "div",
      { css: Styles.content, "data-zds": true },
      label && jsx(
        "div",
        { css: Styles.labelContainer, "data-zds": true },
        jsx(Tag, { variant: labelColor }, labelIcon ? jsx(
          Grid,
          { alignItems: "center", columns: "auto max-content", gap: 5 },
          jsx(Icon, { name: labelIcon, size: 20 }),
          label
        ) : label)
      ),
      jsx(Heading, { css: Styles.heading(backgroundColor) }, heading),
      description && jsx("div", { css: Styles.description(backgroundColor), "data-zds": true }, description),
      ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && jsx("div", { css: Styles.ctaContainer(isSubscriptionForm), "data-zds": true }, isSubscriptionForm ? jsx(SubscriptionFormWithDisclaimer, { placeholderText: subscriptionPlaceholder, onSubmit: onSubscriptionFormSubmit, renderButton: () => getCtaComponent(), disclaimerTextColor: backgroundColor === "neutral100" ? "primary" : "secondary" }) : jsx(
        Fragment,
        null,
        jsx("div", { css: Styles.ctaButtonWrapper, "data-zds": true }, getCtaComponent()),
        ctaDisclaimer && jsx(
          "div",
          { css: Styles.ctaDisclaimer(isSubscriptionForm), "data-zds": true },
          jsx(Text, { tag: "div", type: "Body1", color: mapBackgroundColorToV5HeadingColorName(backgroundColor) }, ctaDisclaimer)
        )
      ))
    )
  );
};

export { HeroFeatures };
//# sourceMappingURL=HeroFeatures.es.js.map
